import { createSlice } from "@reduxjs/toolkit";

import { atomicConfig } from "../../config";
import { call_api_auth } from "../../utils/services";

const initialState = {
  utilizationRisk: null,
  utilizationRiskLoading: false,
  utilizationRiskErrorMessage: null,

  strandedInventory: null,
  strandedInventoryLoading: false,
  strandedInventoryErrorMessage: null,

  overageCapacity: null,
  overageCapacityLoading: false,
  overageCapacityErrorMessage: null,

  removalOrders: null,
  removalOrdersLoading: false,
  removalOrdersErrorMessage: null,
};

const imogenSlice = createSlice({
  name: "imogen",
  initialState,
  reducers: {
    getUtilizationRisk: (state) => {
      state.utilizationRiskLoading = true;
      state.utilizationRiskErrorMessage = null;
    },
    getUtilizationRiskSuccess: (state, { payload }) => {
      state.utilizationRisk = payload;
      state.utilizationRiskLoading = false;
    },
    getUtilizationRiskFailure: (state, { payload }) => {
      state.utilizationRiskLoading = false;
      state.utilizationRiskErrorMessage = payload;
    },

    getStrandedInventory: (state) => {
      state.strandedInventoryLoading = true;
      state.strandedInventoryErrorMessage = null;
    },
    getStrandedInventorySuccess: (state, { payload }) => {
      state.strandedInventory = payload;
      state.strandedInventoryLoading = false;
    },
    getStrandedInventoryFailure: (state, { payload }) => {
      state.strandedInventoryLoading = false;
      state.strandedInventoryErrorMessage = payload;
    },

    getOverageCapacity: (state) => {
      state.overageCapacityLoading = true;
      state.overageCapacityErrorMessage = null;
    },
    getOverageCapacitySuccess: (state, { payload }) => {
      state.overageCapacity = payload;
      state.overageCapacityLoading = false;
    },
    getOverageCapacityFailure: (state, { payload }) => {
      state.overageCapacityLoading = false;
      state.overageCapacityErrorMessage = payload;
    },

    getRemovalOrders: (state) => {
      state.removalOrdersLoading = true;
      state.removalOrdersErrorMessage = null;
    },
    getRemovalOrdersSuccess: (state, { payload }) => {
      state.removalOrders = payload;
      state.removalOrdersLoading = false;
    },
    getRemovalOrdersFailure: (state, { payload }) => {
      state.removalOrdersLoading = false;
      state.removalOrdersErrorMessage = payload;
    },
  },
});

const {
  getUtilizationRisk,
  getUtilizationRiskSuccess,
  getUtilizationRiskFailure,
  getStrandedInventory,
  getStrandedInventorySuccess,
  getStrandedInventoryFailure,
  getOverageCapacity,
  getOverageCapacitySuccess,
  getOverageCapacityFailure,
  getRemovalOrders,
  getRemovalOrdersSuccess,
  getRemovalOrdersFailure,
} = imogenSlice.actions;

export const imogenSelector = (state) => state.imogen;

// The imogen backend expects the raw store code without the `_client` suffix
// that the dashboard uses to flag client stores.
//
// "all" never reaches these endpoints: each one reads a single store, and in the
// path a literal "all" is a store nobody holds a grant on, which the authorizer
// denies before the handler can explain why.
const normalizeStore = (store) =>
  typeof store === "string" ? store.replace(/_client$/, "") : store;

export function fetchUtilizationRisk({ store, marketplace } = {}) {
  return async (dispatch) => {
    dispatch(getUtilizationRisk());
    const storeCode = normalizeStore(store);
    if (!storeCode || storeCode === "all") {
      dispatch(getUtilizationRiskSuccess(null));
      return;
    }
    try {
      const params = new URLSearchParams();
      if (marketplace) params.set("marketplace", marketplace.toUpperCase());
      const query = params.toString() ? `?${params.toString()}` : "";
      const url = `${atomicConfig.storeManagementServiceUrl}/imogen/utilization-risk/${storeCode}${query}`;
      const response = await call_api_auth(url, "GET");
      dispatch(getUtilizationRiskSuccess(response.data));
    } catch (error) {
      dispatch(
        getUtilizationRiskFailure(
          error?.response?.data?.message || error.message || "Request failed"
        )
      );
    }
  };
}

export function fetchStrandedInventory({ store } = {}) {
  return async (dispatch) => {
    dispatch(getStrandedInventory());
    const storeCode = normalizeStore(store);
    if (!storeCode || storeCode === "all") {
      dispatch(getStrandedInventorySuccess(null));
      return;
    }
    try {
      const url = `${atomicConfig.storeManagementServiceUrl}/imogen/stranded-inventory/${storeCode}`;
      const response = await call_api_auth(url, "GET");
      dispatch(getStrandedInventorySuccess(response.data));
    } catch (error) {
      dispatch(
        getStrandedInventoryFailure(
          error?.response?.data?.message || error.message || "Request failed"
        )
      );
    }
  };
}

export function fetchOverageCapacity({ store } = {}) {
  return async (dispatch) => {
    dispatch(getOverageCapacity());
    const storeCode = normalizeStore(store);
    if (!storeCode || storeCode === "all") {
      dispatch(getOverageCapacitySuccess(null));
      return;
    }
    try {
      const url = `${atomicConfig.storeManagementServiceUrl}/imogen/overage-capacity/${storeCode}`;
      const response = await call_api_auth(url, "GET");
      dispatch(getOverageCapacitySuccess(response.data));
    } catch (error) {
      dispatch(
        getOverageCapacityFailure(
          error?.response?.data?.message || error.message || "Request failed"
        )
      );
    }
  };
}

export function fetchRemovalOrders({ store, marketplace } = {}) {
  return async (dispatch) => {
    dispatch(getRemovalOrders());
    const storeCode = normalizeStore(store);
    if (!storeCode || storeCode === "all") {
      dispatch(getRemovalOrdersSuccess(null));
      return;
    }
    try {
      const params = new URLSearchParams();
      if (marketplace) params.set("marketplace", marketplace.toUpperCase());
      const query = params.toString() ? `?${params.toString()}` : "";
      const url = `${atomicConfig.storeManagementServiceUrl}/imogen/removal-orders/${storeCode}${query}`;
      const response = await call_api_auth(url, "GET");
      dispatch(getRemovalOrdersSuccess(response.data));
    } catch (error) {
      dispatch(
        getRemovalOrdersFailure(
          error?.response?.data?.message || error.message || "Request failed"
        )
      );
    }
  };
}

export default imogenSlice.reducer;
